/*
 * D-021 display formats: a place is written with a token template — "{district} – {city}, {country}",
 * "{country} › {city} › {district}", "{postal} {city}, {country:code}". Tokens read the place's own hierarchy, so the
 * same template writes a Turkish, a Dutch and an American place the way each is written.
 *
 * Rules: an empty token disappears together with the separator before it; a value repeated by the next token is
 * written once ("Istanbul, Istanbul" → "Istanbul"); separators never lead or trail. `{token:code}` writes the code
 * (TR, NL-NH, TRIST); `{name}` is the place itself.
 */

export interface PlaceCrumbLike {
  id: string;
  type: string;
  name: string;
  code?: string | null;
}

export interface PlaceLike {
  id: string;
  type: string;
  name: string;
  code?: string | null;
  countryCode?: string | null;
  /** From the root to the place itself. */
  hierarchy: PlaceCrumbLike[];
}

export type PlaceFormatContext = 'short' | 'long' | 'picker' | 'document';

export interface CountryAddressSchema {
  countryCode: string;
  levels: Array<{ type: string; name: Record<string, string> }>;
  required?: string[];
  postalPattern?: string | null;
  templates: Partial<Record<PlaceFormatContext, string>>;
}

export const PLACE_TOKENS = [
  'name',
  'continent',
  'country',
  'admin1',
  'admin2',
  'city',
  'district',
  'neighbourhood',
  'postal',
  'unlocode',
  'site',
  'warehouse',
  'zone',
  'location',
] as const;

/** When a country has no template of its own (and the country schemas have not loaded). */
export const FALLBACK_TEMPLATES: Record<PlaceFormatContext, string> = {
  short: '{name}, {country}',
  long: '{name}, {city}, {admin1}, {country}',
  picker: '{name}',
  document: '{postal} {city}, {country}',
};

const SITE_TYPES = new Set(['warehouse', 'zone', 'location', 'dock', 'yard_slot', 'site', 'address']);
const PORT_TYPES = new Set(['port', 'airport', 'rail_terminal', 'inland_terminal', 'border_crossing', 'unlocode']);

/** The value of one token for a place (undefined when the place has nothing at that level). */
export function tokenValue(place: PlaceLike, token: string, useCode = false): string | undefined {
  const chain = place.hierarchy.length ? place.hierarchy : [{ id: place.id, type: place.type, name: place.name, code: place.code }];
  const find = (...types: string[]) => [...chain].reverse().find((c) => types.includes(c.type));
  const pick = (crumb: PlaceCrumbLike | undefined) => (crumb ? (useCode ? (crumb.code ?? crumb.name) : crumb.name) : undefined);
  switch (token) {
    case 'name':
      return useCode ? (place.code ?? place.name) : place.name;
    case 'continent':
    case 'country':
    case 'admin1':
    case 'admin2':
    case 'district':
    case 'neighbourhood':
    case 'warehouse':
    case 'zone':
    case 'location':
      return pick(find(token));
    case 'city': {
      // Turkey's provinces play the city role (D-031); elsewhere a city, else the district's municipality.
      const city = find('city');
      if (city) return pick(city);
      if ((place.countryCode ?? '').toUpperCase() === 'TR' || chain.some((c) => c.type === 'district')) return pick(find('admin1'));
      return undefined;
    }
    case 'postal': {
      const postal = find('postal_code');
      if (postal) return postal.code ?? postal.name;
      const hood = find('neighbourhood');
      return hood?.code ?? undefined;
    }
    case 'unlocode': {
      const port = [...chain].reverse().find((c) => PORT_TYPES.has(c.type));
      return port?.code ?? undefined;
    }
    case 'site': {
      const site = [...chain].reverse().find((c) => SITE_TYPES.has(c.type));
      return pick(site);
    }
    default:
      return undefined;
  }
}

/** Writes a place with a template (see the module comment for the rules). */
export function formatPlace(place: PlaceLike, template: string): string {
  const parts: Array<{ sep: string; value: string | undefined }> = [];
  const re = /\{([a-z0-9]+)(?::(code))?\}/gi;
  let last = 0;
  let pendingSep = '';
  let match: RegExpExecArray | null;
  while ((match = re.exec(template)) !== null) {
    pendingSep += template.slice(last, match.index);
    parts.push({ sep: pendingSep, value: tokenValue(place, match[1]!.toLowerCase(), match[2] === 'code') });
    pendingSep = '';
    last = re.lastIndex;
  }
  const trailing = template.slice(last);
  let out = '';
  let previous: string | undefined;
  for (const part of parts) {
    if (!part.value) continue;
    if (previous !== undefined && part.value.localeCompare(previous, undefined, { sensitivity: 'base' }) === 0) continue;
    out += out ? part.sep : part.sep.replace(/^[\s,–—›>/|·-]+/, '');
    out += part.value;
    previous = part.value;
  }
  if (!out) return place.name;
  return (out + (parts.length ? trailing.replace(/^[\s,–—›>/|·-]+$/, '') : '')).trim();
}

/** The template for a context: the resolved configuration, else the country's own default, else the fallback. */
export function resolvePlaceTemplate(
  context: PlaceFormatContext,
  configured: Partial<Record<PlaceFormatContext, string>> | undefined,
  schema: CountryAddressSchema | undefined,
): string {
  const own = configured?.[context];
  if (own && own.trim()) return own;
  const fromCountry = schema?.templates?.[context];
  if (fromCountry && fromCountry.trim()) return fromCountry;
  return FALLBACK_TEMPLATES[context];
}

/** What a country calls a place's level ("İlçe", "Bundesland"), in a language. */
export function levelName(schema: CountryAddressSchema | undefined, type: string, lang = 'en'): string | undefined {
  const level = schema?.levels.find((l) => l.type === type);
  return level ? (level.name[lang] ?? level.name.en) : undefined;
}
